import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import Modal, { FullHeightModalContent } from '../../../components/Modal';
import { BodyMedium, Heading5Text } from '../../../components/Texts';
import Button from '../../../components/Button';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../../common/responsive';
import ApplePayMark from '../../../assets/icons/applepay.svg?react';
import { useDispatch } from 'react-redux';
import { closeModal } from '../../../redux/slices/modals';
import { useCallback } from 'react';
import {
  EVENT_APP_STORE_BUTTON_CLICKED,
  sendEvent,
} from '../../../common/analytics';
import { Capacitor } from '@capacitor/core';
import { QRCodeCanvas } from 'qrcode.react';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  padding: 20px;
  width: 100%;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  text-align: center;
  width: 100%;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  @media ${PHONE_MEDIA_QUERY} {
    width: 100%;
  }
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${({ theme }) => theme.border.primary};
  margin: 8px 0;
`;

const QRCodeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 20px;
  background-color: ${({ theme }) => theme.background.secondary};
  border-radius: 12px;
  width: 100%;
`;

const QRCodeWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
  background-color: white;
  border-radius: 8px;
`;

const APP_STORE_URL = 'https://apps.apple.com/us/app/deks-xyz/id6749857801';

const ApplePayDownloadModal = ({ onClose, onPayWithCrypto }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const isWeb = !Capacitor.isNativePlatform();
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const isDesktopWeb = isWeb && !isMobile;

  const handleDownloadApp = useCallback(() => {
    // Send analytics event
    sendEvent(EVENT_APP_STORE_BUTTON_CLICKED);

    // Open App Store (on native and mobile web, desktop web shows QR code)
    if (!isDesktopWeb) {
      window.open(APP_STORE_URL, '_blank');
    }

    // Close modal
    dispatch(closeModal());
  }, [dispatch, isDesktopWeb]);

  const handlePayWithCrypto = useCallback(() => {
    dispatch(closeModal());
    if (onPayWithCrypto) {
      onPayWithCrypto();
    }
  }, [dispatch, onPayWithCrypto]);

  return (
    <Modal onClose={onClose}>
      <FullHeightModalContent>
        <Container>
          <IconContainer>
            <ApplePayMark
              style={{
                width: 60,
                height: 'auto',
              }}
            />
          </IconContainer>
          <ContentContainer>
            <Heading5Text>{t('applePayDownloadModal.title')}</Heading5Text>
            <BodyMedium
              style={{
                color: theme.text.secondary,
                maxWidth: '400px',
              }}
            >
              {isDesktopWeb
                ? t('applePayDownloadModal.descriptionDesktop')
                : t('applePayDownloadModal.descriptionMobile')}
            </BodyMedium>
          </ContentContainer>
          {isDesktopWeb && (
            <QRCodeContainer>
              <BodyMedium
                style={{
                  color: theme.text.secondary,
                  textAlign: 'center',
                }}
              >
                {t('applePayDownloadModal.scanWithPhone')}
              </BodyMedium>
              <QRCodeWrapper>
                <QRCodeCanvas value={APP_STORE_URL} size={200} />
              </QRCodeWrapper>
            </QRCodeContainer>
          )}
          <ButtonContainer>
            <Button
              fullWidth
              clickAnimation='bouncy'
              onClick={handleDownloadApp}
            >
              {isDesktopWeb ? t('applePayDownloadModal.iveDownloadedApp') : t('applePayDownloadModal.downloadIosApp')}
            </Button>
            <Button
              fullWidth
              variant='secondary'
              clickAnimation='bouncy'
              onClick={handlePayWithCrypto}
            >
              {t('applePayDownloadModal.payWithCrypto')}
            </Button>
          </ButtonContainer>
        </Container>
      </FullHeightModalContent>
    </Modal>
  );
};

export default ApplePayDownloadModal;
